import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import logger from '@orn/shared-web/lib/logger';
import * as Sentry from '@sentry/react';
import * as React from 'react';

import type {
  SentryTestDeleteBankConnectionMutation,
  SentryTestDeleteBankConnectionMutationVariables,
} from './__generated__/DebugSentryTestPane.gql';

// `deleteBankConnection` against a non-existent id returns a GraphQL error,
// which the Apollo errorLink forwards to Sentry — exactly what the rehearsal
// needs. The operation name deliberately doesn't start with `admin`:
// `GraphQLMiddleware` would otherwise elevate it to a privileged viewer
// context, and we want this rehearsal to exercise the regular user code path.
const SENTRY_TEST_DELETE_BANK_CONNECTION = gql`
  mutation SentryTestDeleteBankConnection($bankConnectionId: String!) {
    deleteBankConnection(bankConnectionId: $bankConnectionId)
  }
`;

const NON_EXISTENT_UUID = '00000000-0000-0000-0000-000000000000';

// Component that throws an error when rendered
const ErrorComponent = () => {
  throw new Error('Test ErrorBoundary error - This error should be caught by ErrorBoundary and sent to Sentry');
};

const DebugSentryTestPane = () => {
  const [showErrorComponent, setShowErrorComponent] = React.useState(false);
  const [testMessage, setTestMessage] = React.useState<string | null>(null);

  // Mutation that will fail with a non-existent bank connection id — errorPolicy
  // 'none' ensures errors are thrown so the Apollo errorLink reports them.
  const [triggerMutationError] = useMutation<
    SentryTestDeleteBankConnectionMutation,
    SentryTestDeleteBankConnectionMutationVariables
  >(SENTRY_TEST_DELETE_BANK_CONNECTION, {
    errorPolicy: 'none',
  });

  const handleErrorBoundaryError = () => {
    setShowErrorComponent(true);
  };

  const handleGraphQLMutationError = async () => {
    // Apollo Client will catch GraphQL errors via the error link.
    // A non-existent bank connection id returns a "Bank connection not found"
    // error, which the error link forwards to Sentry.
    await triggerMutationError({
      variables: { bankConnectionId: NON_EXISTENT_UUID },
    }).catch(() => {
      setTestMessage('GraphQL mutation error triggered. Check Sentry.');
    });
  };

  const handleNetworkError = async () => {
    // Temporarily break the GraphQL endpoint
    const originalFetch = window.fetch;
    window.fetch = () => {
      return Promise.reject(new Error('Simulated network error'));
    };

    try {
      await triggerMutationError({
        variables: { bankConnectionId: NON_EXISTENT_UUID },
      });
    } catch {
      setTestMessage('Network error triggered. Check Sentry.');
    } finally {
      // Restore original fetch
      window.fetch = originalFetch;
    }
  };

  const handleManualSentryMessage = () => {
    Sentry.captureMessage('Manual test message from Sentry test page', 'info');
    setTestMessage('Manual Sentry message sent. Check Sentry.');
  };

  const handleUnhandledPromiseRejection = () => {
    // Intentionally unhandled promise rejection for testing
    void Promise.reject(new Error('Unhandled promise rejection test error'));
    setTestMessage('Unhandled promise rejection triggered. Check Sentry.');
  };

  const handleUnhandledError = () => {
    setTimeout(() => {
      throw new Error('Unhandled error test - This should be caught by Sentry');
    }, 100);
    setTestMessage('Unhandled error triggered. Check Sentry.');
  };

  const handleLoggerInfo = () => {
    logger.info('Test info message from logger');
    setTestMessage('Logger info message sent. Check Sentry.');
  };

  const handleLoggerWarn = () => {
    logger.warn('Test warning message from logger');
    setTestMessage('Logger warning message sent. Check Sentry.');
  };

  const handleLoggerError = () => {
    logger.error('Test error message from logger', new Error('Test error from logger'));
    setTestMessage('Logger error sent. Check Sentry.');
  };

  // Render error component if triggered
  if (showErrorComponent) {
    return <ErrorComponent />;
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Sentry error testing</div>
        <div className="text-secondary text-sm">
          Use the buttons below to trigger different types of errors and verify they are being sent to Sentry. Check
          your Sentry dashboard to see the errors appear.
        </div>
        {testMessage && <div className="bg-info-primary text-info-primary rounded-lg p-4 text-sm">{testMessage}</div>}
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">React ErrorBoundary errors</div>
          <div className="flex flex-col gap-2">
            <Button onClick={handleErrorBoundaryError} color="primary-destructive" size="md">
              Trigger ErrorBoundary error
            </Button>
            <div className="text-secondary text-xs">
              This will trigger a React component error that should be caught by the ErrorBoundary and sent to Sentry
              with component stack traces.
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">GraphQL errors</div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Button onClick={handleGraphQLMutationError} color="primary-destructive" size="md">
                Trigger GraphQL mutation error
              </Button>
              <div className="text-secondary text-xs">
                This will trigger a GraphQL mutation error using deleteBankConnection with a non-existent bank
                connection id. The error should include mutation details in Sentry.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleNetworkError} color="primary-destructive" size="md">
                Trigger GraphQL network error
              </Button>
              <div className="text-secondary text-xs">
                This will simulate a network error when making a GraphQL request. The error should include network
                context in Sentry.
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">Logger tests</div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Button onClick={handleLoggerInfo} color="primary" size="md">
                Test logger info
              </Button>
              <div className="text-secondary text-xs">
                This will send an info level message using logger.info(). Check Sentry for the info level message.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleLoggerWarn} color="primary" size="md">
                Test logger warning
              </Button>
              <div className="text-secondary text-xs">
                This will send a warning level message using logger.warn(). Check Sentry for the warning level message.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleLoggerError} color="primary-destructive" size="md">
                Test logger error
              </Button>
              <div className="text-secondary text-xs">
                This will send an error using logger.error(). Check Sentry for the exception with logger tag and extra
                message context.
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-xl font-semibold">Other error types</div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Button onClick={handleManualSentryMessage} color="primary" size="md">
                Send manual Sentry message
              </Button>
              <div className="text-secondary text-xs">
                This will send a manual message to Sentry using captureMessage. Useful for testing Sentry connectivity.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleUnhandledPromiseRejection} color="primary-destructive" size="md">
                Trigger unhandled promise rejection
              </Button>
              <div className="text-secondary text-xs">
                This will trigger an unhandled promise rejection. Sentry should automatically capture this.
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleUnhandledError} color="primary-destructive" size="md">
                Trigger unhandled error
              </Button>
              <div className="text-secondary text-xs">
                This will trigger an unhandled error outside of React's error boundary. Sentry should automatically
                capture this.
              </div>
            </div>
          </div>
        </div>

        <div className="border-primary mt-4 rounded-lg border p-4">
          <div className="text-lg font-semibold">Sentry dashboard</div>
          <div className="text-secondary mt-2 text-sm">
            After triggering errors, check your Sentry dashboard to verify:
          </div>
          <ul className="text-secondary mt-2 list-inside list-disc text-sm">
            <li>Errors appear with correct environment tag (dev/staging)</li>
            <li>ErrorBoundary errors have the errorBoundary tag</li>
            <li>GraphQL errors have the graphql tag and include operation details</li>
            <li>Network errors include status codes and network context</li>
            <li>Component stack traces are included for React errors</li>
            <li>Logger errors have the logger tag and include message in extra context</li>
            <li>Logger info and warning messages appear as Sentry messages with appropriate levels</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DebugSentryTestPane;
